import { useMemo } from "react";
import { DashboardRow, HeadCell, SetBarChartData, SetBarChartProps } from "../types/dashboard";

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const useSetBarChartData = (dashboardData: SetBarChartProps["dashboardData"], selectedYear: number): SetBarChartData => {
  const { orders, sales, received, outstanding } = useMemo(() => {
    const initialData = {
      orders: Array(12).fill(0),
      sales: Array(12).fill(0),
      outstanding: Array(12).fill(0),
      received: Array(12).fill(0),
    };

    const monthlyData = dashboardData?.orders_summary || [];
    const calculateMonthlyData = dashboardData?.calculate_monthly_summary || [];

    monthlyData.forEach((summary) => {
      const monthIndex = MONTH_LABELS.indexOf(summary.month);
      if (monthIndex !== -1 && summary.year === selectedYear) {
        initialData.orders[monthIndex] = summary.totalOrders || 0;
        initialData.sales[monthIndex] = summary.totalSales || 0;
        initialData.outstanding[monthIndex] = summary.totalOutstandingBalance || 0;
      }
    });

    calculateMonthlyData.forEach((summary) => {
      const monthIndex = MONTH_LABELS.indexOf(summary.month);
      if (monthIndex !== -1 && summary.year === selectedYear) {
        initialData.received[monthIndex] = summary.total_received || 0;
      }
    });

    return initialData;
  }, [dashboardData, selectedYear]);

  const tableRows: DashboardRow[] = useMemo( () => MONTH_LABELS.map((month, index) => ({
        id: index + 1,
        month,
        totalOrders: orders[index],
        totalSales: sales[index].toFixed(2),
        receivedAmount: received[index].toFixed(2),
        outstandingBalance: outstanding[index].toFixed(2),
      })),
    [orders, sales, received, outstanding]
  );

  const headCells: HeadCell<DashboardRow>[] = useMemo( () => [
      { id: "id", label: "S.No", numeric: false },
      { id: "month", label: "Months", numeric: false },
      { id: "totalOrders", label: "Total Orders", numeric: true, align: "center" },
      { id: "totalSales", label: "Total Sales (QAR)", numeric: true, align: "right" },
      { id: "receivedAmount", label: "Received Amount (QAR)", numeric: true, align: "right" },
      { id: "outstandingBalance", label: "Outstanding Balance (QAR)", numeric: true, align: "right" },
    ], [] );

  return {
    orders,
    sales,
    received,
    outstanding,
    tableRows,
    headCells,
  };
};
